/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, SvgClose, mergeEventHandlers } from '../utils/index.js';
import { IconButton } from '../Buttons/index.js';
import '@itwin/itwinui-css/css/dialog.css';
import { useDialogContext } from './DialogContext.js';
import { DialogTitleBarTitle } from './DialogTitleBarTitle.js';
import { useDialogDragContext } from './DialogDragContext.js';
/**
 * Dialog title bar. Recommended to be used as a child of `Dialog`.
 * @example
 * <Dialog.TitleBar title='My dialog title' />
 * @example
 * <Dialog.TitleBar>
 *   <Dialog.TitleBar.Title>My dialog title</Dialog.TitleBar.Title>
 *   <IconButton
 *     size='small'
 *     styleType='borderless'
 *     onClick={onClose}
 *     aria-label='Close'
 *   >
 *     <SvgClose />
 *   </IconButton>
 * </Dialog.TitleBar>
 */
export const DialogTitleBar = Object.assign(React.forwardRef((props, ref) => {
    const dialogContext = useDialogContext();
    const { children, titleText, isDismissible = dialogContext.isDismissible, onClose = dialogContext.onClose, isDraggable = dialogContext.isDraggable, className, onPointerDown: onPointerDownProp, ...rest } = props;
    const { onPointerDown } = useDialogDragContext();
    useTheme();
    return (React.createElement("div", { className: cx('iui-dialog-title-bar', className, {
            'iui-dialog-title-bar-filled': isDraggable,
        }), ref: ref, onPointerDown: mergeEventHandlers(onPointerDownProp, onPointerDown), ...rest }, children ? (children) : (React.createElement(React.Fragment, null,
        React.createElement(DialogTitleBarTitle, null, titleText),
        isDismissible && (React.createElement(IconButton, { size: 'small', styleType: 'borderless', onClick: onClose, "aria-label": 'Close' },
            React.createElement(SvgClose, null)))))));
}), {
    Title: DialogTitleBarTitle,
});
export default DialogTitleBar;
